import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  MessageSquare,
  ChevronRight,
  ExternalLink,
  Send,
  Loader2,
  Settings,
  Calendar,
  MapPin,
  LinkIcon,
  Plus,
  Trash2,
  Image as ImageIcon,
  Bell,
  Sparkles,
  FileText,
  Heart
} from "lucide-react";
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { TAP_IN_LOGO } from "@/lib/integrationConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Emoji reactions for feedback
const REACTIONS = ['👍', '❤️', '⭐', '🎯', '💡', '🔥'];

const EventPilotPage = () => {
  const navigate = useNavigate();
  const { pilotId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [pilotData, setPilotData] = useState(null);
  const [event, setEvent] = useState(null);
  const [tools, setTools] = useState([]);
  const [screens, setScreens] = useState([]);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [productUpdates, setProductUpdates] = useState([]);
  const [newToolUrl, setNewToolUrl] = useState('');
  const [newToolName, setNewToolName] = useState('');
  const [showAddTool, setShowAddTool] = useState(false);
  const [screenFeedback, setScreenFeedback] = useState({});
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const currentPilot = pilotId || 'hthf';

  useEffect(() => {
    loadPilotData();
  }, [currentPilot]);

  const loadPilotData = async () => {
    setLoading(true);
    try {
      // Load user
      const token = localStorage.getItem('session_token');
      if (token) {
        const userRes = await fetch(`${API}/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (userRes.ok) {
          const userData = await userRes.json();
          setUser(userData);
        }
      }

      // Load HTHF event data
      const eventRes = await fetch(`${API}/events/evt_c1aeb835cadb`);
      if (eventRes.ok) {
        const eventData = await eventRes.json();
        setEvent(eventData.event || eventData);
      }

      // Set pilot data for HTHF
      setPilotData({
        id: 'hthf',
        name: 'High Tech High Fashion',
        subtitle: 'LA Fashion Week 2026',
        category: 'Events',
        type: 'event',
        status: 'active',
        participants: [
          { name: "Event Host", company: "Host Organization", role: "Event Host" }
        ],
        links: [
          { name: "Event Page", url: "/e/hthf-lafw-2026", iconName: "PartyPopper", internal: true },
          { name: "RSVP Page", url: "/e/hthf-lafw-2026?rsvp=true", iconName: "Mail", internal: true },
          { name: "Notion Planning", url: "https://notion.so/hthf-planning", iconName: "FileText", external: true },
          { name: "Canva Assets", url: "https://canva.com/design/hthf", iconName: "Palette", external: true }
        ]
      });

      // Set tools for this pilot
      setTools([
        { id: 1, name: "Tap In", purpose: "Song requests & vibes", url: "https://tapin.app", logo: TAP_IN_LOGO },
        { id: 2, name: "Notion", purpose: "Planning & documentation", url: "https://notion.so", logo: "📝" },
        { id: 3, name: "Canva", purpose: "Design assets", url: "https://canva.com", logo: "🎨" }
      ]);

      // Set screens for user flow
      setScreens([
        { 
          id: "event_page", 
          name: "Event Landing Page", 
          description: "Where attendees first land and RSVP",
          userType: "Attendee Flow",
          status: "live"
        },
        { 
          id: "rsvp_flow", 
          name: "RSVP & Preferences", 
          description: "Fill out dietary & accessibility needs",
          userType: "Attendee Flow",
          status: "live"
        },
        { 
          id: "preference_card", 
          name: "Preference Card", 
          description: "Shareable card with preferences",
          userType: "Attendee Flow",
          status: "live"
        },
        { 
          id: "host_dashboard", 
          name: "Host Dashboard", 
          description: "View RSVPs and manage event",
          userType: "Host Flow",
          status: "live"
        },
        { 
          id: "guest_list", 
          name: "Guest Preferences", 
          description: "See all attendee preferences",
          userType: "Host Flow",
          status: "live"
        },
        { 
          id: "nfc_checkin", 
          name: "NFC Check-In", 
          description: "Tap to check in at event",
          userType: "Event Day",
          status: "beta"
        }
      ]);

      // Set product updates
      setProductUpdates([
        { date: "Feb 9", title: "Preference Card Preview", description: "Click eyeball to preview your card" },
        { date: "Feb 8", title: "Event Pilot Page", description: "New pilot page with feedback system" },
        { date: "Feb 7", title: "Host Dashboard Sync", description: "Guest data now syncs in real-time" }
      ]);

      // Load feedback
      try {
        const fbRes = await fetch(`${API}/admin/pilot-feedback?pilot=${currentPilot}`);
        if (fbRes.ok) {
          const fbData = await fbRes.json();
          setFeedbackItems(fbData.feedback || []);
        }
      } catch (e) {}

    } catch (err) {
      console.error("Failed to load pilot data:", err);
    } finally {
      setLoading(false);
    }
  };

  const addTool = () => {
    if (!newToolName.trim()) return toast.error("Enter tool name");
    setTools([...tools, {
      id: Date.now(),
      name: newToolName,
      purpose: "Custom tool",
      url: newToolUrl || null,
      logo: "🔧",
      isCustom: true
    }]);
    setNewToolName('');
    setNewToolUrl('');
    setShowAddTool(false);
    toast.success("Tool added!");
  };

  const removeTool = (toolId) => {
    setTools(tools.filter(t => t.id !== toolId));
  };

  const submitScreenFeedback = async (screenId) => {
    const text = screenFeedback[screenId];
    if (!text?.trim()) return;

    setSubmittingFeedback(true);
    try {
      await fetch(`${API}/admin/pilot-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pilot: currentPilot,
          screen_id: screenId,
          message: text,
          user_id: user?.user_id,
          created_at: new Date().toISOString()
        })
      });
      toast.success("Feedback submitted!");
      setScreenFeedback({ ...screenFeedback, [screenId]: '' });
      setFeedbackItems([{ screen_id: screenId, message: text, created_at: new Date().toISOString(), reactions: [] }, ...feedbackItems]);
    } catch (err) {
      toast.error("Failed");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Group screens by userType
  const screensByType = screens.reduce((acc, s) => {
    if (!acc[s.userType]) acc[s.userType] = [];
    acc[s.userType].push(s);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Dashboard Header - Like Host Dashboard */}
      <header className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-500 hover:text-gray-900">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back</span>
            </button>
            
            <div className="flex items-center gap-3">
              {user && (
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden">
                    {user.profile_photo_url ? (
                      <img src={user.profile_photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold">
                        {user.name?.[0] || '?'}
                      </div>
                    )}
                  </div>
                  <span className="text-sm font-medium hidden sm:block">{user.name}</span>
                </div>
              )}
              <Button variant="ghost" size="sm">
                <Bell className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Event Info Bar */}
          <div className="flex items-center gap-4 mt-4">
            {/* Square cover image */}
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0 overflow-hidden">
              {event?.cover_image ? (
                <img 
                  src={event.cover_image.startsWith('/') ? `${API}${event.cover_image}` : event.cover_image}
                  alt={event.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white font-bold text-lg">
                  HTHF
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-800 text-xs">Pilot</Badge>
                <Badge variant="outline" className="text-xs">{pilotData?.category}</Badge>
              </div>
              <h1 className="text-xl font-bold">{pilotData?.name}</h1>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                {event?.date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {pilotData?.participants?.length} hosts
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* About Section */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* About Phygital */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-purple-600" />
                <h3 className="font-semibold text-sm">About Phygital</h3>
              </div>
              <p className="text-sm text-gray-600">
                Phygital is the preference layer for seamless info sharing. Users store their preferences once and share with any provider or host. Data syncs everywhere and pre-fills future intakes.
              </p>
            </CardContent>
          </Card>

          {/* About This Pilot */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-purple-600" />
                <h3 className="font-semibold text-sm">About This Pilot</h3>
              </div>
              <p className="text-sm text-gray-600">
                Testing the <strong>Event</strong> category with HTHF at LA Fashion Week. Focusing on preference sharing, NFC check-in, and host dashboard features.
              </p>
              <div className="flex gap-2 mt-3">
                {pilotData?.participants?.map((p, i) => (
                  <div key={i} className="flex items-center gap-1 bg-gray-100 rounded-full px-2 py-1">
                    <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-[10px] font-bold">
                      {p.name[0]}
                    </div>
                    <span className="text-xs">{p.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Links */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <LinkIcon className="w-4 h-4 text-purple-600" />
              Quick Links
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {pilotData?.links?.map((link, idx) => (
                link.internal ? (
                  <Link 
                    key={idx}
                    to={link.url}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <span>{link.icon}</span>
                    <span className="text-sm">{link.name}</span>
                  </Link>
                ) : (
                  <a 
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <span>{link.icon}</span>
                    <span className="text-sm">{link.name}</span>
                    <ExternalLink className="w-3 h-3 text-gray-400" />
                  </a>
                )
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Product Updates */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Heart className="w-4 h-4 text-pink-500" />
              Product Updates from Phygital
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {productUpdates.map((update, idx) => (
                <div key={idx} className="flex items-start gap-3 py-2 border-b last:border-0">
                  <Badge variant="outline" className="text-xs shrink-0">{update.date}</Badge>
                  <div>
                    <p className="text-sm font-medium">{update.title}</p>
                    <p className="text-xs text-gray-500">{update.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Screens & Feedback */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-purple-600" />
              Screens & Feedback
            </CardTitle>
            <CardDescription className="text-xs">
              Provide feedback on each screen in the user flow
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(screensByType).map(([type, typeScreens]) => (
              <div key={type}>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">{type}</p>
                <div className="space-y-2">
                  {typeScreens.map((screen) => {
                    const screenFeedbackList = feedbackItems.filter(f => f.screen_id === screen.id);
                    return (
                      <div key={screen.id} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h4 className="font-medium text-sm">{screen.name}</h4>
                            <p className="text-xs text-gray-500">{screen.description}</p>
                          </div>
                          <Badge className={`text-[10px] ${screen.status === 'live' ? 'bg-green-500' : 'bg-yellow-500'} text-white`}>
                            {screen.status}
                          </Badge>
                        </div>
                        
                        {/* Existing feedback */}
                        {screenFeedbackList.length > 0 && (
                          <div className="mb-2 space-y-1">
                            {screenFeedbackList.slice(0, 2).map((fb, i) => (
                              <div key={i} className="bg-purple-50 rounded px-2 py-1 text-xs">
                                "{fb.message}"
                              </div>
                            ))}
                            {screenFeedbackList.length > 2 && (
                              <span className="text-xs text-gray-400">+{screenFeedbackList.length - 2} more</span>
                            )}
                          </div>
                        )}
                        
                        {/* Add feedback */}
                        <div className="flex gap-2">
                          <Input
                            placeholder="Add feedback..."
                            value={screenFeedback[screen.id] || ''}
                            onChange={(e) => setScreenFeedback({ ...screenFeedback, [screen.id]: e.target.value })}
                            className="h-8 text-xs"
                          />
                          <Button 
                            size="sm" 
                            onClick={() => submitScreenFeedback(screen.id)}
                            disabled={submittingFeedback || !screenFeedback[screen.id]?.trim()}
                            className="h-8 bg-black hover:bg-gray-800"
                          >
                            <Send className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Tools */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                🔧 Tools Used
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => setShowAddTool(true)} className="h-7 text-xs">
                <Plus className="w-3 h-3 mr-1" />Add
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {tools.map((tool) => (
                <div key={tool.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-lg">{tool.logo}</span>
                  <div>
                    <p className="text-sm font-medium">{tool.name}</p>
                    <p className="text-[10px] text-gray-500">{tool.purpose}</p>
                  </div>
                  {tool.url && (
                    <a href={tool.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3 h-3 text-gray-400" />
                    </a>
                  )}
                  {tool.isCustom && (
                    <button onClick={() => removeTool(tool.id)} className="ml-1">
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Add Tool Dialog */}
        <Dialog open={showAddTool} onOpenChange={setShowAddTool}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-base">Add a Tool</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <Input placeholder="Tool name (e.g., Mailchimp)" value={newToolName} onChange={(e) => setNewToolName(e.target.value)} />
              <Input placeholder="Link (optional)" value={newToolUrl} onChange={(e) => setNewToolUrl(e.target.value)} />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowAddTool(false)} className="flex-1">Cancel</Button>
                <Button onClick={addTool} className="flex-1 bg-black hover:bg-gray-800">Add</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default EventPilotPage;
